import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilter,
  faAngleDown,
  faAngleRight,
} from "@fortawesome/free-solid-svg-icons";
import { Button, Badge } from "react-bootstrap";
import styled from "styled-components";
import guLocale from "../locales/gu";
import useOutsideClick from "./useOutsideClick";
import Select from "react-select";

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 5px 30px 5px 25px;
  border: none;
`;

const FilterButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 5px;
  position: relative;
`;

const FilterPopup = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 300px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 10px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const FilterOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  cursor: pointer;
  font-weight: 500;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const DropdownContainer = styled.div`
  padding: 8px;
  background-color: #f9f9f9;
  border-radius: 5px;
`;

const SearchIcon = styled(FontAwesomeIcon)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #777;
`;

const DataTableSearch = React.memo(
  ({
    filterText,
    handleFilterChange,
    handleApplyFilters,
    isFilterVisible = false,
    users,
    heads,
    filterOptions,
  }) => {
    const locale = guLocale.global;
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [selectedDropdown, setSelectedDropdown] = useState(null);
    const [selectedFilters, setSelectedFilters] = useState({
      head: [],
      user: [],
    });
    const filterRef = useRef(null);
    useOutsideClick(filterRef, () => {
      setShowFilterPopup(false);
      setSelectedDropdown(null);
    });

    const toggleDropdown = (type) => {
      setSelectedDropdown((prev) => (prev === type ? null : type));
    };

    // const handleFilterSelect = (e, type) => {
    //   setSelectedFilters({ ...selectedFilters, [type]: e.target.value });
    // };

    const applyFilters = () => {
      handleApplyFilters(selectedFilters);
      setShowFilterPopup(false);
    };

    const resetFilters = () => {
      setSelectedFilters({ head: "", user: "" });
      handleApplyFilters({ head: "", user: "" });
    };

    const selectedCount =
      selectedFilters.head.length + selectedFilters.user.length;

    return (
      <SearchContainer>
        <SearchInput
          type="text"
          placeholder={locale.searchLabel}
          value={filterText}
          onChange={handleFilterChange}
          className="form-control datatable-input-search"
        />
        <SearchIcon icon={faSearch} />
        <div
          style={{
            position: "relative",
            visibility: isFilterVisible ? "visible" : "hidden",
          }}
          ref={filterRef}
        >
          <FilterButton
            variant="outline-default"
            onClick={(e) => {
              e.stopPropagation(); // Prevents event from reaching useOutsideClick
              setShowFilterPopup((prev) => !prev);
            }}
          >
            <FontAwesomeIcon icon={faFilter} style={{ color: "#495057" }} />
            {selectedCount > 0 && <Badge bg="danger">{selectedCount}</Badge>}
          </FilterButton>

          {showFilterPopup && (
            <FilterPopup>
              {filterOptions.showHeads && (
                <>
                  <FilterOption onClick={() => toggleDropdown("head")}>
                    <span>
                      {selectedDropdown === "head" ? (
                        <FontAwesomeIcon icon={faAngleDown} />
                      ) : (
                        <FontAwesomeIcon icon={faAngleRight} />
                      )}
                      હેડ
                    </span>
                  </FilterOption>
                  {selectedDropdown === "head" && (
                    <DropdownContainer>
                      <Select
                        isMulti
                        options={heads.map((head) => ({
                          value: head._id,
                          label: head.head_name,
                        }))}
                        value={selectedFilters.head}
                        onChange={(selectedOptions) => {
                          setSelectedFilters((prev) => ({
                            ...prev,
                            head: selectedOptions || [], // Ensure empty array if all are unselected
                          }));
                        }}
                        placeholder={locale.selectHead}
                      />
                    </DropdownContainer>
                  )}
                </>
              )}

              {filterOptions.showUsers && (
                <>
                  <FilterOption onClick={() => toggleDropdown("user")}>
                    <span>
                      {selectedDropdown === "user" ? (
                        <FontAwesomeIcon icon={faAngleDown} />
                      ) : (
                        <FontAwesomeIcon icon={faAngleRight} />
                      )}
                      વપરાશકર્તાઓ
                    </span>
                  </FilterOption>
                  {selectedDropdown === "user" && (
                    <DropdownContainer>
                      <Select
                        isMulti
                        options={users.map((user) => ({
                          value: user._id,
                          label: user.school_name,
                        }))}
                        value={selectedFilters.user}
                        onChange={(selectedOptions) => {
                          setSelectedFilters((prev) => ({
                            ...prev,
                            user: selectedOptions || [], // Ensure empty array if all are unselected
                          }));
                        }}
                        placeholder={locale.selectUser}
                      />
                    </DropdownContainer>
                  )}
                </>
              )}

              <div className="d-flex justify-content-between mt-3">
                <Button variant="secondary" size="sm" onClick={resetFilters}>
                  રીસેટ કરો
                </Button>
                <Button variant="primary" size="sm" onClick={applyFilters}>
                  લાગુ કરો
                </Button>
              </div>
            </FilterPopup>
          )}
        </div>
      </SearchContainer>
    );
  }
);

export default DataTableSearch;
